/**
 * When a consultant can submit their review, kept out of the dialog so the rules are readable and
 * testable on their own -- every document type reads them differently, and the Bluebeam options
 * cut across all of them.
 */
export type ConsultantReviewStatus = 'in-progress' | 'file' | 'studio';

export type ConsultantReviewSubmitState = {
  /** Which of the review radio options is selected. */
  reviewStatus: ConsultantReviewStatus;
  /** A file was picked AND its upload to staging finished. */
  hasVerifiedUpload: boolean;
  /**
   * A file was picked, whether or not its upload finished. Only the Final pay application reads
   * this -- it has always accepted the selection alone.
   */
  hasUploadSelection: boolean;
  /** Bluebeam was launched and the version it produces will be posted when this review is sent. */
  hasBluebeamMarkup: boolean;
  /** The .bax Bluebeam writes back has finished uploading, so the markup can be flattened. */
  isBluebeamMarkupReady: boolean;
  hasComment: boolean;
  hasActionTaken: boolean;
  isPackage: boolean;
  /** Every submittal in the package has a recommended action. */
  hasAllPackageActions: boolean;
  isRfi: boolean;
  isPayApp: boolean;
  isFinalPayApp: boolean;
};

export function isConsultantReviewSubmitDisabled(state: ConsultantReviewSubmitState): boolean {
  const {
    reviewStatus,
    hasVerifiedUpload,
    hasUploadSelection,
    hasBluebeamMarkup,
    isBluebeamMarkupReady,
    hasComment,
    hasActionTaken,
    isPackage,
    hasAllPackageActions,
    isRfi,
    isPayApp,
    isFinalPayApp,
  } = state;

  const readyForReview = reviewStatus !== 'in-progress';
  // Either way of posting a file from this dialog satisfies the same requirements: an upload that
  // has landed, or markups that will be flattened into a new version when the review is sent.
  const hasFile = hasVerifiedUpload || hasBluebeamMarkup;

  // A pending Bluebeam markup is posted as part of submitting, so the review waits for the .bax
  // file Bluebeam writes back to finish uploading before it can be submitted.
  if (hasBluebeamMarkup && !isBluebeamMarkupReady) return true;

  // Markups made in a Bluebeam Studio session are already on the file, so that option needs
  // neither a file posted here nor a comment -- only the recommended action, where one applies.
  if (reviewStatus === 'studio') {
    if (isPackage) return !hasAllPackageActions;
    if (isRfi) return false;
    return !hasActionTaken;
  }

  // An RFI is answered with a comment, a file, or both -- on this type a comment alone is a
  // complete review.
  if (isRfi) return !hasComment && !hasFile;

  if (isFinalPayApp) {
    return !(hasUploadSelection || hasBluebeamMarkup) || (readyForReview && !hasActionTaken);
  }

  if (isPayApp) return (!hasComment && !hasFile) || (readyForReview && !hasActionTaken);

  if (readyForReview) {
    if (isPackage && !hasAllPackageActions) return true;

    // "Review complete, file uploaded" means exactly that: a file posted from here, either
    // uploaded or marked up in Bluebeam. Returning a review off the back of a Bluebeam Studio
    // session is its own option, so it no longer rides on a comment here.
    return !(hasFile && (hasActionTaken || isPackage));
  }

  return false;
}
